import { observer } from "mobx-react";
import { isMobileView } from "@/utils/screen.ts";
import { MTextInput } from "@/components-v2/TextInput.tsx";
import { Box, Container } from "@/components-v2/Box.tsx";
import { useTranslation } from "@/stores/RootStoreContext.ts";
import { MSelect } from "@/components-v2/Select.tsx";
import { DataTable, TableColumn } from "@/components-v2/DataTable.tsx";
import { Checkbox } from "@/components-v2/Checkbox.tsx";
import { DropdownMenu } from "@/components-v2/Dropdown.tsx";
import {
  shareholderCompanyEntries,
  shareholderEmail,
  shareholderLastLogin,
  shareholderRepresentative,
  ShareholdersViewProps,
} from "./shareholdersDomain.ts";
import { AggregatedContactItem } from "../contactsDomain.ts";
import { TextWithTooltip } from "@/components-v2/TextWithTooltip.tsx";
import { LastLogin } from "@/common/commonComponents.tsx";
import { ShareholderCompanyEntry } from "./shareholdersDomain.ts";
import { ShareholderExcelResultDialog } from "@/shareholderRegister/shareholders/ShareholderExcelResultDialog.tsx";

export const ShareholdersView = observer((props: ShareholdersViewProps) => {
  const { t } = useTranslation();
  const columns: TableColumn<AggregatedContactItem>[] = [
    {
      name: "name",
      label: t("name"),
      align: "left",
      getValue: (row) => row.name,
      getSortValue: (row) => row.name,
    },
    {
      name: "representative",
      label: t("portfolioContacts.representative"),
      align: "left",
      getValue: (row) => shareholderRepresentative(row.ownershipItems),
      getSortValue: (row) => shareholderRepresentative(row.ownershipItems) ?? "",
    },
    {
      name: "companies",
      label: t("portfolioContacts.companies"),
      align: "left",
      getValue: (row) => (
        <ShareholderCompaniesData
          companies={shareholderCompanyEntries(row.ownershipItems, props.shareSummaries)}
        />
      ),
      getSortValue: (row) =>
        shareholderCompanyEntries(row.ownershipItems, props.shareSummaries)
          .map((e) => e.companyName)
          .join(", "),
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
      getValue: (row) => <TextWithTooltip {...shareholderEmail(row.ownershipItems)} />,
      getSortValue: (row) => t(shareholderEmail(row.ownershipItems).text),
    },
    {
      name: "lastLogin",
      label: t("portfolioContacts.last-login"),
      align: "left",
      getValue: (row) => <LastLogin lastLogin={shareholderLastLogin(row.ownershipItems)} />,
      getSortValue: (row) => shareholderLastLogin(row.ownershipItems) ?? "",
    },
  ];

  return (
    <Container padding maxWidth="xl">
      <Box vertical={isMobileView()} alignItems={"center"}>
        {props.searchFieldModel && (
          <Box vertical={isMobileView()}>
            <MTextInput
              model={props.searchFieldModel}
              icon={"magnifying-glass"}
              placeholder={t("portfolioContacts.nameFilterPlaceholder")}
              type={"search"}
            />
          </Box>
        )}
        <Box vertical={isMobileView()}>
          {props.shareholderCompanyFilter && <MSelect model={props.shareholderCompanyFilter} />}
          {props.investmentStatusModel && <MSelect model={props.investmentStatusModel} />}
        </Box>
        <Box marginLeft={isMobileView() ? undefined : "lg"}>
          <Checkbox
            checked={props.filterMissingEmailModel?.value}
            label={props.filterMissingEmailLabel}
            disabled={props.isFilterMissingEmailDisabled}
            onChange={(e) => props.filterMissingEmailModel?.setValue(e.target.checked)}
          />
        </Box>
        {props.editWithExcelMenu && (
          <Box vertical={isMobileView()} marginLeft={"auto"}>
            <DropdownMenu {...props.editWithExcelMenu} />
          </Box>
        )}
      </Box>
      <DataTable
        variant={"LARGE"}
        tableColumns={columns}
        tableData={props.aggregatedPersonItems}
        rowFilter={props.rowFilter}
        getId={(row) => row.aggregatedContactId}
        initialSortBy={"name"}
        initialSortDirection={"asc"}
        onRowClick={(row) => props.navigateToDetails(row)}
        rowActions={props.rowActions}
      />
      {props.excelResult && (
        <ShareholderExcelResultDialog
          result={props.excelResult}
          onClose={props.onCloseExcelResult}
        />
      )}
    </Container>
  );
});

function ShareholderCompaniesData({ companies }: { companies: ShareholderCompanyEntry[] }) {
  const { t } = useTranslation();
  if (companies.length === 1) {
    return (
      <Box>
        <div>{companies[0].companyName}</div>
        <Box fg={"subtle"}>{t({ type: "percent", value: companies[0].percent })}</Box>
      </Box>
    );
  }
  return (
    <TextWithTooltip
      text={{ key: "portfolio.numberOfCompanies", params: { count: companies.length } }}
      tooltip={{
        type: "md-list",
        value: companies.map(
          (e) => `${e.companyName}  ${t({ type: "percent", value: e.percent })}`,
        ),
      }}
    />
  );
}
